import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link2 } from 'lucide-react'
import { type ReactNode } from 'react'

import { Button, InlineAlert } from '@aeris/ui'

import { EdgeError, edgePair } from '../lib/edge-api.ts'
import { ShellConfigError, writeEdgeToken } from '../lib/shell-config.ts'

/**
 * Parear este caixa com o Edge da loja (F197).
 *
 * O Edge só atende caixa pareado, e até aqui nenhuma tela pareava — o balcão
 * "tinha" modo sem internet e não vendia nada nele. Parear é com a internet no
 * ar: o Edge pergunta à nuvem quem é a pessoa logada (o aplicativo repassa o
 * cookie), exige que ela opere o PDV, e devolve uma credencial. A credencial
 * vai direto para o shell, que a guarda e a põe em cada chamada ao Edge; a
 * página nunca a guarda.
 */
export const EdgePairing = ({
  terminalId,
}: {
  terminalId: string
}): ReactNode => {
  const queryClient = useQueryClient()

  const pair = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async () => {
      const token = await edgePair(terminalId)
      await writeEdgeToken(token)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['edge', 'sync-status'] })
      await queryClient.invalidateQueries({ queryKey: ['shell-config'] })
    },
  })

  const failure =
    pair.error instanceof EdgeError || pair.error instanceof ShellConfigError
      ? pair.error.message
      : pair.error === null
        ? null
        : 'Não foi possível parear agora. Confira o computador da loja e tente de novo.'

  return (
    <InlineAlert
      tone="warning"
      title="Sem internet, este caixa ainda não vende"
      action={
        <Button
          variant="primary"
          size="sm"
          loading={pair.isPending}
          disabled={terminalId === ''}
          leadingIcon={<Link2 aria-hidden="true" className="h-4 w-4" />}
          onClick={() => {
            pair.mutate()
          }}
        >
          Parear com o Edge da loja
        </Button>
      }
    >
      {terminalId === ''
        ? 'Escolha o terminal deste caixa e pareie com o computador da loja enquanto a internet está no ar.'
        : `Pareie o ${terminalId} com o computador da loja enquanto a internet está no ar — é o que deixa vender quando ela cair.`}
      {failure === null ? null : (
        <span role="alert" className="mt-1 block text-(--danger)">
          {failure}
        </span>
      )}
    </InlineAlert>
  )
}
